import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/authContext";
import { getUserAllocations } from "../services/manageSitesService";

const normalizeAllocations = (allocations = []) =>
	(Array.isArray(allocations) ? allocations : [])
		.map((allocation) => ({
			cus_id: Number(allocation?.cus_id),
			site_id: Number(allocation?.site_id),
		}))
		.filter(
			(allocation) =>
				Number.isFinite(allocation.cus_id) &&
				allocation.cus_id > 0 &&
				Number.isFinite(allocation.site_id) &&
				allocation.site_id > 0
		);

const useActorAllocationScope = () => {
	const { user, hasPermission } = useAuth();
	const [fallbackAllocations, setFallbackAllocations] = useState([]);
	const [scopeResolved, setScopeResolved] = useState(false);

	const isSuperAdmin = Number(user?.user_id) === 1;
	const canManageAllSites = hasPermission("user_management.manage_all_sites");
	const actorRoleType = useMemo(
		() =>
			String(
				user?.role?.role_type ||
				user?.role?.role_name ||
				user?.user_role_name ||
				user?.user_role ||
				""
			)
				.trim()
				.toLowerCase(),
		[user?.role?.role_type, user?.role?.role_name, user?.user_role_name, user?.user_role]
	);

	const userAllocations = useMemo(
		() => normalizeAllocations(user?.allocations),
		[user?.allocations]
	);
	const needsFallbackFetch = Boolean(
		user?.user_id &&
		!isSuperAdmin &&
		!canManageAllSites &&
		userAllocations.length === 0
	);

	useEffect(() => {
		let mounted = true;

		const loadAllocations = async () => {
			if (!needsFallbackFetch) {
				if (mounted) {
					setFallbackAllocations([]);
					setScopeResolved(true);
				}
				return;
			}

			try {
				const response = await getUserAllocations(user.user_id);
				if (mounted) {
					setFallbackAllocations(normalizeAllocations(response?.data?.allocations));
				}
			} catch (error) {
				console.error("Failed to load actor allocations", error);
				if (mounted) setFallbackAllocations([]);
			} finally {
				if (mounted) setScopeResolved(true);
			}
		};

		if (mounted) {
			setScopeResolved(!needsFallbackFetch);
		}
		loadAllocations();
		return () => {
			mounted = false;
		};
	}, [needsFallbackFetch, user?.user_id]);

	const effectiveAllocations = useMemo(
		() => (userAllocations.length > 0 ? userAllocations : fallbackAllocations),
		[userAllocations, fallbackAllocations]
	);

	const actorSiteIds = useMemo(
		() =>
			Array.from(
				new Set(
					effectiveAllocations
						.map((allocation) => Number(allocation.site_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			),
		[effectiveAllocations]
	);

	const actorCustomerIds = useMemo(
		() =>
			Array.from(
				new Set(
					effectiveAllocations
						.map((allocation) => Number(allocation.cus_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			),
		[effectiveAllocations]
	);

	const actorHasAllSiteVisibility = useMemo(
		() =>
			isSuperAdmin ||
			canManageAllSites ||
			(actorRoleType === "admin" && actorSiteIds.length === 0),
		[actorRoleType, actorSiteIds.length, canManageAllSites, isSuperAdmin]
	);
	const scopeLoading = !scopeResolved;

	return {
		scopeLoading,
		isSuperAdmin,
		actorRoleType,
		effectiveAllocations,
		actorSiteIds,
		actorCustomerIds,
		actorHasAllSiteVisibility,
	};
};

export default useActorAllocationScope;
